import { useId } from "react";
import { Box, Divider, Link, List, ListItem, Stack } from "@mui/material";
import { useTranslation } from "next-i18next";
import { IconMail } from "src/components/icons-mui/mail";
import { IconPhone } from "src/components/icons-mui/phone";
import { IconFacebook } from "src/components/icons-mui/social/facebook";
import { IconInstagram } from "src/components/icons-mui/social/instagram";
import { IconLinkedIn } from "src/components/icons-mui/social/linkedin";
import { IconTwitter } from "src/components/icons-mui/social/twitter";
import { IconYoutube } from "src/components/icons-mui/social/youtube";
import { CurrencySelector } from "src/components/layout/footer/currency-selector";
import { WfNativeSelect } from "src/components-mui/base/wf-native-select/wf-native-select";
import { Container } from "src/components-mui/layout/container";
import { extractLocale } from "src/constants/locale";
import { useDisclaimerStore } from "src/stores/disclaimer-store";
import { IFooter } from "src/types/layout";
import { FOOTER_SELECT_COUNTRY_TEST_ID } from "./footer.test-ids";

const DIVIDER_COLOR = "gray.300";

const SOCIAL_ICON_LINK_SX = {
    fontSize: "md",
    "&:hover": { color: "gray.600" },
};

const FOOTER_LINK_SX = {
    color: "gray.600",
    fontWeight: "normal",
};

const Footer = (props: IFooter) => {
    const { linkList, currencies, currentIso, countries, email, phone, bottomLinkList } = props;
    const { t } = useTranslation("common");
    const disclaimerStore = useDisclaimerStore();
    const id = useId();

    return (
        <Box component="footer" sx={{ backgroundColor: "gray.100", marginTop: "auto" }}>
            <Container sx={{ py: 5 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 3, sm: 2 }} sx={{ justifyContent: "space-between" }}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={{ xs: 3, sm: 4 }}
                        divider={
                            <Divider
                                sx={{
                                    boxSizing: "border-box",
                                    borderColor: { xs: DIVIDER_COLOR, sm: "transparent" },
                                    borderWidth: { xs: "0 0 1px 0", sm: "0 1px 0 0" },
                                    width: { xs: "auto", sm: "1px" },
                                    height: { xs: "1px", sm: "auto" },
                                    alignSelf: "stretch",
                                }}
                            />
                        }
                    >
                        {linkList.map(item => (
                            <Box key={item.title} sx={{ textAlign: { xs: "center", sm: "left" } }}>
                                <Box sx={{ fontWeight: "bold", mb: { xs: 2, sm: 3 } }}>{item.title}</Box>
                                <List sx={{ p: 0, "& > li + li": { mt: 2 } }}>
                                    {item.links.map(link => (
                                        <ListItem
                                            key={link.url}
                                            disablePadding
                                            sx={{
                                                display: "block",
                                                textAlign: { xs: "center", sm: "left" },
                                            }}
                                        >
                                            <Link href={link.url} target={link.target} underline="hover" sx={FOOTER_LINK_SX}>
                                                {link.name}
                                            </Link>
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        ))}
                    </Stack>
                    <Stack sx={{ flexShrink: 0, alignItems: "flex-end" }}>
                        <Box sx={{ width: { xs: "100%", sm: "auto" }, mb: 2 }}>
                            <WfNativeSelect
                                id={id}
                                name={`footer-country-select-${id}`}
                                size="small"
                                value={currentIso}
                                onChange={e => {
                                    const { country } = extractLocale(e.target.value);
                                    disclaimerStore.tryChangeCountry(country, false);
                                }}
                                aria-label={t("labels.select-country")}
                                data-test-id={FOOTER_SELECT_COUNTRY_TEST_ID}
                                inputSx={{
                                    backgroundColor: "transparent",
                                    height: "40px !important",
                                    borderColor: "gray.800",
                                    lineHeight: theme => theme.spacing(3),
                                }}
                                sx={{
                                    fontSize: theme => theme.typography.sm,
                                    fontWeight: theme => theme.typography.fontWeightSemibold,
                                    backgroundColor: "transparent",
                                    "& .MuiNativeSelect-select": {
                                        borderColor: "gray.800",
                                    },
                                }}
                            >
                                {countries.map(country => (
                                    <option key={country.value} value={country.value}>
                                        {country.label}
                                    </option>
                                ))}
                            </WfNativeSelect>
                        </Box>
                        <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
                            <CurrencySelector currencies={currencies} />
                        </Box>
                    </Stack>
                </Stack>
                <Divider sx={{ borderColor: { xs: "transparent", sm: DIVIDER_COLOR }, my: { xs: 2, sm: 5 } }} />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={4} sx={{ justifyContent: "space-between", alignItems: "center" }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 3, sm: 4 }} sx={{ alignItems: "center" }}>
                        <Link href={phone.url} target={phone.target} underline="hover" className="gtm-phone">
                            <IconPhone sx={{ width: "24px", height: "24px", mr: 1 }} />
                            {phone.name}
                        </Link>
                        <Link href={email.url} target={email.target} underline="hover" className="gtm-contact__mail">
                            <IconMail sx={{ width: "24px", height: "24px", mr: 1 }} />
                            {email.name}
                        </Link>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Link
                            href="https://www.facebook.com/wikifolio"
                            target="_blank"
                            rel="noreferrer noopener"
                            underline="none"
                            aria-label={t("follow-facebook")}
                            className="gtm-footer__social-icon"
                            sx={SOCIAL_ICON_LINK_SX}
                        >
                            <IconFacebook sx={{ width: "24px", height: "24px", color: "inherit" }} />
                        </Link>
                        <Link
                            href="https://www.instagram.com/wikifolio/"
                            target="_blank"
                            rel="noreferrer noopener"
                            underline="none"
                            aria-label={t("follow-instagram")}
                            className="gtm-footer__social-icon"
                            sx={SOCIAL_ICON_LINK_SX}
                        >
                            <IconInstagram sx={{ width: "24px", height: "24px" }} />
                        </Link>
                        <Link
                            href="https://twitter.com/wikifolio"
                            target="_blank"
                            rel="noreferrer noopener"
                            underline="none"
                            aria-label={t("follow-twitter")}
                            className="gtm-footer__social-icon"
                            sx={SOCIAL_ICON_LINK_SX}
                        >
                            <IconTwitter sx={{ width: "24px", height: "24px" }} />
                        </Link>
                        <Link
                            href="https://www.linkedin.com/company/2486880/"
                            target="_blank"
                            rel="noreferrer noopener"
                            underline="none"
                            aria-label={t("follow-linked-in")}
                            className="gtm-footer__social-icon"
                            sx={SOCIAL_ICON_LINK_SX}
                        >
                            <IconLinkedIn sx={{ width: "24px", height: "24px" }} />
                        </Link>
                        <Link
                            href="https://www.youtube.com/user/wikifoliocom"
                            target="_blank"
                            rel="noreferrer noopener"
                            underline="none"
                            aria-label={t("follow-youtube")}
                            className="gtm-footer__social-icon"
                            sx={SOCIAL_ICON_LINK_SX}
                        >
                            <IconYoutube sx={{ width: "24px", height: "24px" }} />
                        </Link>
                    </Stack>
                </Stack>
                <Stack direction={{ xs: "column", sm: "row-reverse" }} sx={{ justifyContent: "space-between", alignItems: "center", mt: 4 }}>
                    <Stack direction="row" spacing={2} sx={{ mb: { xs: 4, sm: 0 }, alignItems: "center" }}>
                        {bottomLinkList.map(link => (
                            <Link key={link.url} href={link.url} target={link.target} underline="hover" sx={FOOTER_LINK_SX}>
                                {link.name}
                            </Link>
                        ))}
                    </Stack>
                    <Box sx={{ textAlign: "center", fontSize: "sm", color: "gray.600" }}>
                        {new Date().getFullYear()} © wikifolio Financial Technologies AG
                    </Box>
                </Stack>
            </Container>
        </Box>
    );
};

export { Footer };
